import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { moodAPI, medicationsAPI, appointmentsAPI } from '../services/api';
import { Brain, Pill, Calendar, MessageCircle, AlertCircle, TrendingUp, ShoppingBag, Bot, MessagesSquare, FileText, Shield, Crown, Check, Sparkles, Lock, BookOpen, Stethoscope, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MindPulse } from '../components/MindPulse';
import FeatureCategories from '../components/FeatureCategories';

export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [moodSummary, setMoodSummary] = useState(null);
  const [medications, setMedications] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorCode, setDoctorCode] = useState('');
  const [connectingDoctor, setConnectingDoctor] = useState(false);
  const [connectedDoctors, setConnectedDoctors] = useState([]);

  const isNormalUser = user?.role === 'user';
  const isPro = user?.role === 'superuser' || user?.role === 'admin';
  const isDoctor = user?.user_type === 'doctor';
  
  // Check if user has permission for a feature
  const hasPermission = (feature) => {
    if (isPro) return true;
    return user?.permissions?.[feature] === true;
  };

  // Handle click on premium feature card
  const handleFeatureClick = (feature, path) => {
    if (hasPermission(feature)) {
      navigate(path);
    } else {
      toast.error('This feature requires a Pro subscription', {
        action: {
          label: 'View Plans',
          onClick: () => navigate('/pricing')
        }
      });
      navigate('/pricing');
    }
  };

  // Connect to doctor with code
  const handleConnectToDoctor = async (e) => {
    e.preventDefault();
    if (!doctorCode.trim()) {
      toast.error('Please enter a doctor code');
      return;
    }
    setConnectingDoctor(true);
    try {
      const response = await api.post('/doctor-portal/patient/connect', { code: doctorCode.toUpperCase() });
      toast.success(response.data.message || 'Connection request sent! Waiting for doctor approval.');
      setDoctorCode('');
      fetchConnectedDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect. Invalid or expired code.');
    } finally {
      setConnectingDoctor(false);
    }
  };

  // Fetch connected doctors
  const fetchConnectedDoctors = async () => {
    if (isDoctor) return;
    try {
      const response = await api.get('/doctor-portal/connections?status=approved');
      setConnectedDoctors(response.data.connections || []);
    } catch (error) {
      console.log('Doctor connections not available');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    if (!isDoctor) {
      fetchConnectedDoctors();
    }
  }, [isDoctor]);

  const fetchDashboardData = async () => {
    try {
      // Fetch mood summary (not subscription gated)
      const moodRes = await moodAPI.getSummary('week');
      setMoodSummary(moodRes.data);
    } catch (error) {
      console.error('Failed to fetch mood summary:', error);
    }
    
    try {
      // Fetch medications (subscription gated - may fail for regular users)
      const medsRes = await medicationsAPI.getAll();
      setMedications(medsRes.data.slice(0, 3));
    } catch (error) {
      // Silently handle - user may not have subscription
      console.log('Medications require subscription');
      setMedications([]);
    }
    
    try {
      // Fetch appointments (not subscription gated)
      const aptsRes = await appointmentsAPI.getAll();
      setUpcomingAppointments(aptsRes.data.filter(apt => 
        apt.status === 'scheduled' || apt.status === 'confirmed'
      ).slice(0, 2));
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Welcome back, {user?.full_name?.split(' ')[0] || 'Friend'}
          </h1>
          <p className="text-muted-foreground">Here's your mental wellness overview</p>
        </div>

        {/* Connect to Doctor Card - Only for normal users (non-doctors) */}
        {!isDoctor && (
          <div className="mb-8 feature-card" data-testid="connect-doctor-card">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.15)' }}>
                <Stethoscope className="w-6 h-6" style={{ color: 'var(--sunrise-orange)' }} />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-black)', fontFamily: 'Nunito, sans-serif' }}>Connect to Your Doctor</h2>
                <p className="text-sm mb-4" style={{ color: 'var(--text-medium)' }}>
                  Enter the code from your healthcare provider to share your wellness data
                </p>
                <form onSubmit={handleConnectToDoctor} className="flex gap-2">
                  <input
                    type="text"
                    value={doctorCode}
                    onChange={(e) => setDoctorCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-digit code"
                    className="flex-1 max-w-xs px-4 py-2.5 rounded-xl uppercase tracking-wider font-mono input-sunrise"
                    maxLength={6}
                    data-testid="doctor-code-input"
                  />
                  <button
                    type="submit"
                    disabled={connectingDoctor || !doctorCode.trim()}
                    className="px-5 py-2.5 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 btn-primary-cta"
                    data-testid="connect-doctor-btn"
                  >
                    {connectingDoctor ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Link2 className="w-5 h-5" />
                    )}
                    Connect
                  </button>
                </form>
              </div>
            </div>

            {/* Connected Doctors */}
            {connectedDoctors.length > 0 && (
              <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--card-border)' }}>
                <p className="text-xs mb-2" style={{ color: 'var(--text-light)' }}>Connected Doctors:</p>
                <div className="flex flex-wrap gap-2">
                  {connectedDoctors.map(conn => (
                    <span key={conn.id} className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(255, 140, 0, 0.15)', color: 'var(--sunrise-dark-orange)' }}>
                      Dr. {conn.doctor_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <QuickActionCard
            to="/mood-journal"
            icon={<Brain className="w-6 h-6" />}
            title="Log Mood"
            description="Track your feelings"
            iconColor="#FFA500"
            bgColor="rgba(255, 165, 0, 0.15)"
          />
          <QuickActionCard
            to="/medications"
            icon={<Pill className="w-6 h-6" />}
            title="Medications"
            description="Manage your meds"
            iconColor="#22C55E"
            bgColor="rgba(34, 197, 94, 0.15)"
            locked={!hasPermission('medications')}
            onClick={() => handleFeatureClick('medications', '/medications')}
          />
          <QuickActionCard
            to="/appointments"
            icon={<Calendar className="w-6 h-6" />}
            title="Find Help"
            description="Connect with professionals"
            iconColor="#3B82F6"
            bgColor="rgba(59, 130, 246, 0.15)"
          />
          <QuickActionCard
            to="/talk"
            icon={<MessageCircle className="w-6 h-6" />}
            title="Just Talk"
            description="Connect with peers"
            iconColor="#FF8C00"
            bgColor="rgba(255, 140, 0, 0.15)"
            locked={!hasPermission('talk')}
            onClick={() => handleFeatureClick('talk', '/talk')}
          />
        </div>

        {/* MindPulse Widget - Premium Feature (Positioned Above Other Features) */}
        <div className="mb-6">
          <MindPulse />
        </div>

        {/* Secondary Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <QuickActionCard
            to="/amigo"
            icon={<Bot className="w-6 h-6" />}
            title="Amigo AI"
            description="Chat with your companion"
            iconColor="#A855F7"
            bgColor="rgba(168, 85, 247, 0.15)"
            locked={!hasPermission('ai_companion')}
            onClick={() => handleFeatureClick('ai_companion', '/amigo')}
          />
          <QuickActionCard
            to="/forums"
            icon={<MessagesSquare className="w-6 h-6" />}
            title="Forums"
            description="Share & connect"
            iconColor="#22C55E"
            bgColor="rgba(34, 197, 94, 0.15)"
            locked={!hasPermission('talk')}
            onClick={() => handleFeatureClick('talk', '/forums')}
          />
          <QuickActionCard
            to="/doctor-summary"
            icon={<FileText className="w-6 h-6" />}
            title="Doctor Summary"
            description="Prepare for appointments"
            iconColor="#06B6D4"
            bgColor="rgba(6, 182, 212, 0.15)"
            locked={!hasPermission('doctor_summary')}
            onClick={() => handleFeatureClick('doctor_summary', '/doctor-summary')}
          />
          <QuickActionCard
            to="/vault"
            icon={<Shield className="w-6 h-6" />}
            title="Private Vault"
            description="Encrypted journal"
            iconColor="#8B5CF6"
            bgColor="rgba(139, 92, 246, 0.15)"
            locked={!hasPermission('private_vault')}
            onClick={() => handleFeatureClick('private_vault', '/vault')}
          />
        </div>

        {/* Main Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Mood Summary - Larger Card */}
          <div className="md:col-span-2 feature-card" data-testid="mood-summary-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                Weekly Mood
              </h2>
              <TrendingUp className="w-6 h-6" style={{ color: 'var(--sunrise-orange)' }} />
            </div>
            {moodSummary && moodSummary.count > 0 ? (
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-5xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--sunrise-dark-orange)' }}>
                    {moodSummary.average.toFixed(1)}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--text-medium)' }}>
                    Average mood score<br />
                    <span className="font-semibold" style={{ color: 'var(--text-black)' }}>{moodSummary.count} entries this week</span>
                  </div>
                </div>
                <Link 
                  to="/mood-journal" 
                  className="text-sm font-semibold hover:underline"
                  style={{ color: 'var(--sunrise-dark-orange)' }}
                  data-testid="view-mood-details"
                >
                  View detailed trends →
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
                <p className="mb-4" style={{ color: 'var(--text-light)' }}>No mood entries yet this week</p>
                <Link 
                  to="/mood-journal" 
                  className="px-6 py-2 rounded-full inline-block text-white btn-primary-cta"
                  data-testid="log-first-mood"
                >
                  Log your first mood
                </Link>
              </div>
            )}
          </div>

          {/* Emergency Quick Access */}
          <Link
            to="/emergency"
            className="rounded-2xl p-6 card-soft text-white hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #EF4444 0%, #FF8C00 100%)' }}
            data-testid="emergency-quick-access"
          >
            <AlertCircle className="w-8 h-8 mb-3" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Emergency
            </h3>
            <p className="text-sm text-white/90">Quick access to crisis resources</p>
          </Link>

          {/* Store Quick Access */}
          <Link
            to="/store"
            className="rounded-2xl p-6 card-soft text-white hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)' }}
            data-testid="store-quick-access"
          >
            <ShoppingBag className="w-8 h-8 mb-3" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Wellness Store
            </h3>
            <p className="text-sm text-white/90">Browse helpful products</p>
          </Link>

          {/* Content Library Quick Access */}
          <Link
            to="/content"
            className="rounded-2xl p-6 card-soft text-white hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #0891B2 0%, #3B82F6 100%)' }}
            data-testid="content-library-quick-access"
          >
            <BookOpen className="w-8 h-8 mb-3" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Content Library
            </h3>
            <p className="text-sm text-white/90">Articles, exercises & guides</p>
          </Link>

          {/* Quantum Manifestation Card - Pro Feature */}
          {isPro && (
            <Link
              to="/quantum-manifestation"
              className="rounded-2xl p-6 card-soft text-white hover:scale-105 transition-transform"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)' }}
              data-testid="quantum-quick-access"
            >
              <Sparkles className="w-8 h-8 mb-3" />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Quantum Manifestation
              </h3>
              <p className="text-sm text-white/90">Guided techniques & intentions</p>
            </Link>
          )}

          {/* Medications Card */}
          <div className="md:col-span-2 feature-card" data-testid="medications-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                Medications
              </h2>
              <Pill className="w-6 h-6" style={{ color: '#22C55E' }} />
            </div>
            {medications.length > 0 ? (
              <div className="space-y-3">
                {medications.map((med) => (
                  <div key={med.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
                    <div>
                      <div className="font-semibold" style={{ color: 'var(--text-black)' }}>{med.name}</div>
                      <div className="text-sm" style={{ color: 'var(--text-medium)' }}>{med.dosage} - {med.frequency}</div>
                    </div>
                  </div>
                ))}
                <Link 
                  to="/medications" 
                  className="text-sm font-semibold block mt-2 hover:underline"
                  style={{ color: '#22C55E' }}
                  data-testid="view-all-medications"
                >
                  View all medications →
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <Pill className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
                <p className="mb-4" style={{ color: 'var(--text-light)' }}>No medications added</p>
                <Link 
                  to="/medications" 
                  className="px-6 py-2 rounded-full text-white inline-block"
                  style={{ background: '#22C55E' }}
                  data-testid="add-first-medication"
                >
                  Add medication
                </Link>
              </div>
            )}
          </div>

          {/* Appointments Card */}
          <div className="md:col-span-2 feature-card" data-testid="appointments-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                My Appointments
              </h2>
              <Calendar className="w-6 h-6" style={{ color: '#3B82F6' }} />
            </div>
            {upcomingAppointments.length > 0 ? (
              <div className="space-y-3">
                {upcomingAppointments.map((apt) => (
                  <div key={apt.id} className="p-3 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
                    <div className="font-semibold" style={{ color: 'var(--text-black)' }}>{apt.provider_name}</div>
                    <div className="text-sm" style={{ color: 'var(--text-medium)' }}>
                      {new Date(apt.appointment_date).toLocaleDateString()} - {apt.provider_specialty}
                    </div>
                  </div>
                ))}
                <Link 
                  to="/appointments" 
                  className="text-sm font-semibold block mt-2 hover:underline"
                  style={{ color: '#3B82F6' }}
                  data-testid="view-all-appointments"
                >
                  View all appointments →
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
                <p className="mb-4" style={{ color: 'var(--text-light)' }}>No upcoming appointments</p>
                <Link 
                  to="/appointments" 
                  className="px-6 py-2 rounded-full text-white inline-block"
                  style={{ background: '#3B82F6' }}
                  data-testid="book-first-appointment"
                >
                  Find professional help
                </Link>
              </div>
            )}
          </div>

          {/* Plans & Pricing Card - Only for Normal Users */}
          {isNormalUser && (
            <div className="md:col-span-2 feature-card" style={{ borderLeft: '4px solid var(--sunrise-dark-orange)' }} data-testid="pricing-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255, 140, 0, 0.15)' }}>
                    <Crown className="w-6 h-6" style={{ color: 'var(--sunrise-orange)' }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                      Upgrade to Pro
                    </h2>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Unlock all premium features</p>
                  </div>
                </div>
                <span className="px-3 py-1 text-xs font-bold rounded-full" style={{ background: 'rgba(255, 140, 0, 0.15)', color: 'var(--sunrise-dark-orange)' }}>
                  LIMITED OFFER
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { name: 'Weekly', price: '$6', period: '/week' },
                  { name: 'Monthly', price: '$12', period: '/month' },
                  { name: 'Quarterly', price: '$30', period: '/3mo', popular: true },
                  { name: 'Yearly', price: '$100', period: '/year' }
                ].map((plan) => (
                  <div 
                    key={plan.name}
                    className="p-3 rounded-xl text-center"
                    style={{ 
                      background: plan.popular ? 'rgba(255, 140, 0, 0.15)' : 'rgba(255, 165, 0, 0.08)',
                      border: plan.popular ? '2px solid var(--sunrise-dark-orange)' : 'none'
                    }}
                  >
                    {plan.popular && (
                      <span className="text-[10px] font-bold block mb-1" style={{ color: 'var(--sunrise-dark-orange)' }}>POPULAR</span>
                    )}
                    <div className="text-lg font-bold" style={{ color: 'var(--text-black)' }}>{plan.price}</div>
                    <div className="text-xs" style={{ color: 'var(--text-light)' }}>{plan.period}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {['AI Companion', 'Peer Support', 'Medication Tracking', 'Wellness Store'].map((feature) => (
                  <span key={feature} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255, 165, 0, 0.1)', color: 'var(--text-dark)' }}>
                    <Check className="w-3 h-3" style={{ color: '#22C55E' }} />
                    {feature}
                  </span>
                ))}
              </div>

              <Link
                to="/pricing"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold btn-primary-cta"
                data-testid="view-pricing-btn"
              >
                <Sparkles className="w-5 h-5" />
                View All Plans
              </Link>
            </div>
          )}
        </div>

        {/* Explore All Features Section */}
        <div className="mt-12 mb-8">
          <FeatureCategories />
        </div>
      </div>
    </div>
  );
};

const QuickActionCard = ({ to, icon, title, description, iconColor, bgColor, locked, onClick }) => {
  if (locked) {
    return (
      <div
        onClick={onClick}
        className="feature-card hover:scale-105 transition-transform cursor-pointer relative opacity-70"
        data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="absolute top-3 right-3">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(234, 179, 8, 0.2)' }}>
            <Lock className="w-3 h-3" style={{ color: '#EAB308' }} />
          </div>
        </div>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: bgColor }}>
          <div style={{ color: iconColor }}>{icon}</div>
        </div>
        <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>{title}</h3>
        <p className="text-sm" style={{ color: 'var(--text-medium)' }}>{description}</p>
        <span className="text-xs mt-2 block" style={{ color: '#EAB308' }}>Pro Feature</span>
      </div>
    );
  }
  
  return (
    <Link
      to={to}
      className="feature-card hover:scale-105 transition-transform"
      data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: bgColor }}>
        <div style={{ color: iconColor }}>{icon}</div>
      </div>
      <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>{title}</h3>
      <p className="text-sm" style={{ color: 'var(--text-medium)' }}>{description}</p>
    </Link>
  );
};
